import { cConst, cGradient, cRange, defaultLayer, gradient, vConst, vRange } from '../defaults.js';
import { curvePreset } from '../math/curve.js';
/**
 * Environment library (plan §4.1 expansion): looping ambient set-dressing.
 * These run forever, so the craft is in seamless loops (rate emitters, no
 * burst pops), stable population budgets, and layered slow/fast motion so the
 * eye never catches a repeat.
 */
/** Toon fire gradient shared by the fire effects: white core → yellow → orange → red → gone. */
const FIRE_GRADIENT = gradient('stepped', [0, [1, 0.98, 0.9, 1]], [0.25, [1, 0.85, 0.35, 1]], [0.55, [1, 0.55, 0.12, 0.95]], [0.8, [0.85, 0.25, 0.05, 0.7]], [1, [0.4, 0.1, 0.05, 0]]);
/** Campfire: layered toon flames, bright core, wandering embers, lazy smoke and a flickering light pool. */
export const bonfireEffect = {
    version: 1,
    meta: { name: 'Bonfire', duration: 2, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'bo_flames',
            name: 'flame tongues',
            delay: 0,
            duration: 2,
            maxParticles: 40,
            emitter: {
                mode: { type: 'rate', perSecond: 13 },
                shape: { type: 'cone', angle: 8, radius: 0.24 },
                initial: {
                    lifetime: vRange(0.55, 0.9),
                    speed: vRange(0.9, 1.6),
                    spread: vConst(5),
                    size: vRange(0.5, 0.8),
                    rotation: vRange(-10, 10),
                    angularVelocity: vRange(-25, 25),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 0.8 },
                { type: 'turbulence', enabled: true, frequency: 1.6, amplitude: 1.1, scrollSpeed: 0.5 },
                {
                    // taper as they rise — flame silhouette
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.55, outTan: 2.5 }, { t: 0.3, v: 1, inTan: 0, outTan: 0 }, { t: 1, v: 0.15, inTan: -1.4 }] },
                },
                { type: 'colorOverLife', enabled: true, gradient: FIRE_GRADIENT },
            ],
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'fireSheet' } },
        }),
        defaultLayer({
            id: 'bo_core',
            name: 'hot core',
            delay: 0,
            duration: 2,
            maxParticles: 16,
            emitter: {
                mode: { type: 'rate', perSecond: 9 },
                shape: { type: 'cone', angle: 6, radius: 0.16 },
                initial: {
                    lifetime: vRange(0.3, 0.5),
                    speed: vRange(0.6, 1),
                    spread: vConst(4),
                    size: vRange(0.28, 0.45),
                    rotation: vConst(0),
                    color: cRange([1, 1, 0.92, 1], [1, 0.93, 0.6, 1]),
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('fadeInOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.8, 0.3, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'bo_embers',
            name: 'embers',
            delay: 0,
            duration: 2,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 7 },
                shape: { type: 'cone', angle: 14, radius: 0.28 },
                initial: {
                    lifetime: vRange(1, 1.8),
                    speed: vRange(1.4, 2.4),
                    spread: vConst(10),
                    size: vRange(0.03, 0.07),
                    rotation: vConst(0),
                    color: cRange([1, 0.8, 0.4, 1], [1, 0.55, 0.2, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.9 },
                { type: 'drag', enabled: true, coefficient: 0.7 },
                { type: 'turbulence', enabled: true, frequency: 1.1, amplitude: 1.8, scrollSpeed: 0.4 },
                {
                    // ember blink: brightness stutters on the way up
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('stepped', [0, [1, 1, 1, 1]], [0.35, [1, 0.9, 0.6, 0.7]], [0.55, [1, 1, 0.8, 1]], [0.75, [1, 0.7, 0.4, 0.5]], [1, [0.8, 0.4, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'bo_smoke',
            name: 'lazy smoke',
            delay: 0,
            duration: 2,
            maxParticles: 12,
            emitter: {
                mode: { type: 'rate', perSecond: 3.5 },
                shape: { type: 'cone', angle: 10, radius: 0.18 },
                initial: {
                    lifetime: vRange(1.6, 2.3),
                    speed: vRange(0.8, 1.2),
                    spread: vConst(8),
                    size: vRange(0.6, 1),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-35, 35),
                    color: cConst([0.5, 0.48, 0.5, 0.4]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'turbulence', enabled: true, frequency: 0.6, amplitude: 0.5, scrollSpeed: 0.2 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'bo_light',
            name: 'light pool',
            delay: 0,
            duration: 2,
            maxParticles: 8,
            emitter: {
                // overlapping short-lived glows read as natural firelight flicker
                mode: { type: 'rate', perSecond: 5 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vRange(0.3, 0.5),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(1.6, 2.1),
                    rotation: vConst(0),
                    color: cRange([1, 0.75, 0.4, 0.28], [1, 0.6, 0.25, 0.2]),
                },
            },
            renderer: { type: 'procMesh', shape: { kind: 'quad', orientation: 'ground' } },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.4, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/** Wall-torch flame: a compact three-layer bonfire tuned for props. */
export const torchFlameEffect = {
    version: 1,
    meta: { name: 'Torch Flame', duration: 1.5, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'to_flame',
            name: 'flame',
            delay: 0,
            duration: 1.5,
            maxParticles: 22,
            emitter: {
                mode: { type: 'rate', perSecond: 14 },
                shape: { type: 'cone', angle: 7, radius: 0.09 },
                initial: {
                    lifetime: vRange(0.35, 0.55),
                    speed: vRange(0.7, 1.1),
                    spread: vConst(5),
                    size: vRange(0.16, 0.26),
                    rotation: vRange(-10, 10),
                    color: cConst([1, 1, 1, 1]),
                },
            },
            modules: [
                { type: 'turbulence', enabled: true, frequency: 2.2, amplitude: 0.5, scrollSpeed: 0.7 },
                {
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.6, outTan: 2.5 }, { t: 0.3, v: 1, inTan: 0, outTan: 0 }, { t: 1, v: 0.12, inTan: -1.5 }] },
                },
                { type: 'colorOverLife', enabled: true, gradient: FIRE_GRADIENT },
            ],
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'fireSheet' } },
        }),
        defaultLayer({
            id: 'to_embers',
            name: 'stray embers',
            delay: 0,
            duration: 1.5,
            maxParticles: 8,
            emitter: {
                mode: { type: 'rate', perSecond: 2.5 },
                shape: { type: 'cone', angle: 12, radius: 0.08 },
                initial: {
                    lifetime: vRange(0.6, 1),
                    speed: vRange(0.8, 1.3),
                    spread: vConst(12),
                    size: vRange(0.02, 0.04),
                    rotation: vConst(0),
                    color: cRange([1, 0.85, 0.45, 1], [1, 0.6, 0.25, 1]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: -0.5 },
                { type: 'turbulence', enabled: true, frequency: 1.6, amplitude: 1, scrollSpeed: 0.5 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 0.5, 0.1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'hardDot' } },
        }),
        defaultLayer({
            id: 'to_halo',
            name: 'halo',
            delay: 0,
            duration: 1.5,
            maxParticles: 6,
            emitter: {
                mode: { type: 'rate', perSecond: 4 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vRange(0.25, 0.45),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.55, 0.75),
                    rotation: vConst(0),
                    color: cRange([1, 0.8, 0.45, 0.5], [1, 0.65, 0.3, 0.35]),
                },
            },
            modules: [
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.4, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
    ],
};
/**
 * Rainfall: streaked drops sheet down and each death spawns a splash ring —
 * the sub-emitter hookup doing environment work. Tuned so drops land ~1 unit
 * below the emitter; raise the emitter and lengthen `lifetime` for real cover.
 */
export const rainfallEffect = {
    version: 1,
    meta: { name: 'Rainfall', duration: 1, loop: true, simulationSpace: 'world' },
    layers: [
        defaultLayer({
            id: 'ra_drops',
            name: 'raindrops',
            delay: 0,
            duration: 1,
            maxParticles: 96,
            emitter: {
                mode: { type: 'rate', perSecond: 110 },
                shape: { type: 'box', size: [4.5, 0.1, 4.5] },
                initial: {
                    lifetime: vRange(0.24, 0.3),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.03, 0.05),
                    rotation: vConst(0),
                    color: cRange([0.65, 0.78, 1, 0.75], [0.5, 0.65, 0.95, 0.75]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.5, 0.7) },
            modules: [
                { type: 'gravity', enabled: true, strength: 30 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.2, [1, 1, 1, 1]], [1, [1, 1, 1, 1]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'ra_splashes',
            name: 'splash rings',
            delay: 0,
            duration: 1,
            maxParticles: 80,
            emitter: {
                trigger: { parent: 'ra_drops', on: 'death' },
                mode: { type: 'burst', count: 1 },
                shape: { type: 'point' },
                initial: {
                    lifetime: vRange(0.18, 0.28),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.14, 0.22),
                    rotation: vConst(0),
                    color: cConst([0.7, 0.85, 1, 0.7]),
                },
            },
            renderer: {
                type: 'procMesh',
                shape: {
                    kind: 'ring',
                    segments: 24,
                    width: { preset: 'custom', points: [{ t: 0, v: 0.6, outTan: 0 }, { t: 1, v: 0.1, inTan: -0.8 }] },
                    widthScale: 0.05,
                },
            },
            modules: [
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'procedural', hardness: 0 } },
        }),
    ],
};
/** Snowfall: two depth layers of drifting flakes with turbulent flutter and terminal-velocity drag. */
export const snowfallEffect = {
    version: 1,
    meta: { name: 'Snowfall', duration: 3, loop: true, simulationSpace: 'world' },
    layers: [
        defaultLayer({
            id: 'sn_near',
            name: 'near flakes',
            delay: 0,
            duration: 3,
            maxParticles: 110,
            emitter: {
                mode: { type: 'rate', perSecond: 26 },
                shape: { type: 'box', size: [5, 0.2, 5] },
                initial: {
                    lifetime: vRange(2.6, 3.6),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.035, 0.07),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-90, 90),
                    color: cRange([1, 1, 1, 0.95], [0.85, 0.92, 1, 0.95]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 1 },
                { type: 'drag', enabled: true, coefficient: 1.1 },
                { type: 'turbulence', enabled: true, frequency: 0.45, amplitude: 1, scrollSpeed: 0.15 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.12, [1, 1, 1, 1]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'sn_far',
            name: 'far flakes',
            delay: 0,
            duration: 3,
            maxParticles: 80,
            emitter: {
                mode: { type: 'rate', perSecond: 18 },
                shape: { type: 'box', size: [6, 0.2, 6] },
                initial: {
                    lifetime: vRange(2.8, 3.8),
                    speed: vConst(0),
                    spread: vConst(0),
                    size: vRange(0.02, 0.035),
                    rotation: vConst(0),
                    color: cConst([0.9, 0.95, 1, 0.55]),
                },
            },
            modules: [
                { type: 'gravity', enabled: true, strength: 0.8 },
                { type: 'drag', enabled: true, coefficient: 1 },
                { type: 'turbulence', enabled: true, frequency: 0.35, amplitude: 0.7, scrollSpeed: 0.1 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.12, [1, 1, 1, 1]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
/** Fireflies: sparse wandering glows that blink on and off — pure gradient-alpha choreography. */
export const firefliesEffect = {
    version: 1,
    meta: { name: 'Fireflies', duration: 4, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'fl_flies',
            name: 'fireflies',
            delay: 0,
            duration: 4,
            maxParticles: 24,
            emitter: {
                mode: { type: 'rate', perSecond: 6 },
                shape: { type: 'box', size: [3.2, 1.6, 3.2] },
                initial: {
                    lifetime: vRange(2.4, 3.8),
                    speed: vRange(0.1, 0.25),
                    spread: vConst(0),
                    size: vRange(0.08, 0.14),
                    rotation: vConst(0),
                    color: cRange([1, 0.95, 0.45, 1], [0.75, 1, 0.4, 1]),
                },
            },
            modules: [
                { type: 'turbulence', enabled: true, frequency: 0.55, amplitude: 0.55, scrollSpeed: 0.12 },
                { type: 'drag', enabled: true, coefficient: 0.4 },
                {
                    // double blink over each firefly's life
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.12, [1, 1, 1, 1]], [0.3, [1, 1, 1, 0.05]], [0.5, [1, 1, 1, 0.9]], [0.68, [1, 1, 1, 0.05]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'glow' } },
        }),
        defaultLayer({
            id: 'fl_dust',
            name: 'ambient dust',
            delay: 0,
            duration: 4,
            maxParticles: 20,
            emitter: {
                mode: { type: 'rate', perSecond: 4 },
                shape: { type: 'box', size: [3.5, 1.8, 3.5] },
                initial: {
                    lifetime: vRange(2, 3.2),
                    speed: vRange(0.03, 0.1),
                    spread: vConst(0),
                    size: vRange(0.015, 0.035),
                    rotation: vConst(0),
                    color: cConst([0.9, 0.92, 1, 0.4]),
                },
            },
            modules: [
                { type: 'turbulence', enabled: true, frequency: 0.4, amplitude: 0.2, scrollSpeed: 0.08 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [0.75, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
/** Falling leaves: tumbling quads fluttering down through layered turbulence in autumn tints. */
export const fallingLeavesEffect = {
    version: 1,
    meta: { name: 'Falling Leaves', duration: 4, loop: true, simulationSpace: 'world' },
    layers: [
        defaultLayer({
            id: 'lv_leaves',
            name: 'leaves',
            delay: 0,
            duration: 4,
            maxParticles: 40,
            emitter: {
                mode: { type: 'rate', perSecond: 7 },
                shape: { type: 'box', size: [4, 0.3, 4] },
                initial: {
                    lifetime: vRange(3, 4.2),
                    speed: vRange(0.05, 0.2),
                    spread: vConst(0),
                    size: vRange(0.09, 0.16),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-220, 220),
                    // stepped autumn palette — every leaf is one of four exact tints
                    color: cGradient(gradient('stepped', [0, [0.95, 0.6, 0.2, 1]], [0.25, [0.85, 0.35, 0.12, 1]], [0.5, [0.98, 0.8, 0.3, 1]], [0.75, [0.62, 0.28, 0.14, 1]])),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'quad' } },
            modules: [
                { type: 'gravity', enabled: true, strength: 1.4 },
                { type: 'drag', enabled: true, coefficient: 1.3 },
                { type: 'turbulence', enabled: true, frequency: 0.5, amplitude: 2.2, scrollSpeed: 0.18 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.1, [1, 1, 1, 1]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.75 } },
        }),
        defaultLayer({
            id: 'lv_gold',
            name: 'golden stragglers',
            delay: 0,
            duration: 4,
            maxParticles: 16,
            emitter: {
                mode: { type: 'rate', perSecond: 2.5 },
                shape: { type: 'box', size: [4.5, 0.3, 4.5] },
                initial: {
                    lifetime: vRange(3.4, 4.4),
                    speed: vRange(0.05, 0.15),
                    spread: vConst(0),
                    size: vRange(0.07, 0.12),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-150, 150),
                    color: cRange([1, 0.85, 0.3, 1], [0.95, 0.7, 0.2, 1]),
                },
            },
            renderer: { type: 'mesh', mesh: { type: 'builtin', id: 'quad' } },
            modules: [
                { type: 'gravity', enabled: true, strength: 1.1 },
                { type: 'drag', enabled: true, coefficient: 1.4 },
                { type: 'turbulence', enabled: true, frequency: 0.65, amplitude: 2.6, scrollSpeed: 0.22 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.1, [1, 1, 1, 1]], [0.85, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'procedural', hardness: 0.75 } },
        }),
    ],
};
/** Steam vent: a hissing jet that blooms into billows, with occasional hot spits. */
export const steamVentEffect = {
    version: 1,
    meta: { name: 'Steam Vent', duration: 1.6, loop: true, simulationSpace: 'local' },
    layers: [
        defaultLayer({
            id: 'st_jet',
            name: 'pressure jet',
            delay: 0,
            duration: 1.6,
            maxParticles: 44,
            emitter: {
                mode: { type: 'rate', perSecond: 26 },
                shape: { type: 'cone', angle: 6, radius: 0.09 },
                initial: {
                    lifetime: vRange(0.5, 0.85),
                    speed: vRange(3, 4.4),
                    spread: vConst(4),
                    size: vRange(0.14, 0.3),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-90, 90),
                    color: cRange([1, 1, 1, 0.65], [0.85, 0.9, 0.95, 0.65]),
                },
            },
            modules: [
                { type: 'drag', enabled: true, coefficient: 2.6 },
                { type: 'gravity', enabled: true, strength: -0.6 },
                {
                    type: 'sizeOverLife',
                    enabled: true,
                    curve: { preset: 'custom', points: [{ t: 0, v: 0.5, outTan: 2 }, { t: 1, v: 2.2, inTan: 1.4 }] },
                },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0.9]], [0.6, [1, 1, 1, 0.55]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'softDot' } },
        }),
        defaultLayer({
            id: 'st_billow',
            name: 'billows',
            delay: 0.1,
            duration: 1.5,
            maxParticles: 16,
            emitter: {
                mode: { type: 'rate', perSecond: 6 },
                shape: { type: 'cone', angle: 18, radius: 0.12 },
                initial: {
                    lifetime: vRange(1, 1.5),
                    speed: vRange(1, 1.6),
                    spread: vConst(10),
                    size: vRange(0.45, 0.8),
                    rotation: vRange(0, 360),
                    angularVelocity: vRange(-45, 45),
                    color: cConst([0.95, 0.97, 1, 0.5]),
                },
            },
            renderer: { type: 'flipbook', rows: 4, cols: 4, playback: { mode: 'lifetime' }, blendFrames: true },
            modules: [
                { type: 'drag', enabled: true, coefficient: 1.5 },
                { type: 'gravity', enabled: true, strength: -0.8 },
                { type: 'turbulence', enabled: true, frequency: 0.8, amplitude: 0.6, scrollSpeed: 0.25 },
                { type: 'sizeOverLife', enabled: true, curve: curvePreset('easeOut') },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 0]], [0.25, [1, 1, 1, 1]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'alpha', sprite: { type: 'builtin', id: 'smokeSheet' } },
        }),
        defaultLayer({
            id: 'st_spits',
            name: 'hot spits',
            delay: 0,
            duration: 1.6,
            maxParticles: 10,
            emitter: {
                mode: { type: 'rate', perSecond: 4 },
                shape: { type: 'cone', angle: 13, radius: 0.06 },
                initial: {
                    lifetime: vRange(0.35, 0.6),
                    speed: vRange(4, 6),
                    spread: vConst(8),
                    size: vRange(0.025, 0.05),
                    rotation: vConst(0),
                    color: cConst([1, 1, 1, 0.9]),
                },
            },
            renderer: { type: 'stretchedBillboard', stretch: vRange(0.3, 0.5) },
            modules: [
                { type: 'gravity', enabled: true, strength: 10 },
                {
                    type: 'colorOverLife',
                    enabled: true,
                    gradient: gradient('smooth', [0, [1, 1, 1, 1]], [0.8, [1, 1, 1, 0.8]], [1, [1, 1, 1, 0]]),
                },
            ],
            material: { blend: 'additive', sprite: { type: 'builtin', id: 'softDot' } },
        }),
    ],
};
